import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ENDPOINTS, resolveAssetUrl } from '../config';

function formatDate(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return '';
    }
}

export default function Blog() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await fetch(ENDPOINTS.BLOGS.PUBLIC);
                const data = await res.json();
                if (data.isOk && Array.isArray(data.data)) {
                    setBlogs(data.data);
                }
            } catch (err) {
                console.error('Error fetching blogs:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    return (
        <main className="pt-[10px] pb-12">
            <div className="max-w-[1400px] mx-auto px-4">
                <nav className="py-4 text-sm">
                    <ol className="flex items-center gap-2 text-muted-foreground">
                        <li>
                            <Link to="/" className="hover:text-primary transition-colors">
                                Home
                            </Link>
                        </li>
                        <li>
                            <i className="ri-arrow-right-s-line"></i>
                        </li>
                        <li className="text-foreground font-medium">Blog</li>
                    </ol>
                </nav>

                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-foreground">Blog</h1>
                </div>

                {loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="bg-card border border-border rounded-lg overflow-hidden animate-pulse"
                            >
                                <div className="w-full h-48 bg-muted"></div>
                                <div className="p-5 space-y-3">
                                    <div className="h-5 bg-muted rounded w-3/4"></div>
                                    <div className="h-4 bg-muted rounded w-1/2"></div>
                                    <div className="h-10 bg-muted rounded w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && blogs.length === 0 && (
                    <div className="text-center py-16">
                        <i className="ri-article-line text-4xl text-muted-foreground/40 mb-4 block"></i>
                        <h3 className="text-xl font-semibold text-foreground mb-2">
                            No blog posts yet
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            Check back soon for guides and updates.
                        </p>
                        <Link
                            to="/products"
                            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-gradient-brand text-primary-foreground text-sm font-semibold"
                        >
                            Browse Products
                        </Link>
                    </div>
                )}

                {!loading && blogs.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {blogs.map((blog) => {
                            const imageUrl = resolveAssetUrl(blog.featuredImage);
                            const tags = Array.isArray(blog.tags) ? blog.tags : [];
                            return (
                                <Link
                                    key={blog._id}
                                    to={`/blog/${blog.slug}`}
                                    className="group bg-card border border-border rounded-lg overflow-hidden hover:border-primary/40 transition-colors flex flex-col"
                                >
                                    <div className="relative w-full h-48 bg-muted overflow-hidden">
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={blog.title}
                                                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                                <i className="ri-article-line text-4xl"></i>
                                            </div>
                                        )}
                                        {tags.length > 0 && (
                                            <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1.5">
                                                {tags.slice(0, 3).map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="px-2 py-0.5 rounded bg-black/70 text-white text-[11px] font-medium backdrop-blur-sm"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-5 flex flex-col flex-1">
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
                                            {(blog.publishDate || blog.createdAt) && (
                                                <span>{formatDate(blog.publishDate || blog.createdAt)}</span>
                                            )}
                                            {blog.category && (
                                                <>
                                                    <span>·</span>
                                                    <span>{blog.category}</span>
                                                </>
                                            )}
                                        </div>
                                        <h2 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                                            {blog.title}
                                        </h2>
                                        {blog.excerpt && (
                                            <p className="text-sm text-muted-foreground line-clamp-3 flex-1">
                                                {blog.excerpt}
                                            </p>
                                        )}
                                        {blog.author && (
                                            <p className="mt-3 text-xs text-muted-foreground">
                                                By {blog.author}
                                            </p>
                                        )}
                                        <span className="mt-4 text-sm font-semibold text-primary inline-flex items-center gap-1">
                                            Read more
                                            <i className="ri-arrow-right-line"></i>
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </main>
    );
}
